import { ReportDataSet, RiskMetricId } from '@/constants/ReportDashboard/api-enums'
import { GfrItem } from '@/constants/ReportDashboard/gfr-items'
import { AllReportTotalItems, ReportItem, ReportTotalItem } from '@/constants/ReportDashboard/report-items'
import { IGfpaFactorContributionFreq, IGfpaFactorContributionTotal, IGfpaSecurityContrib } from '@/interfaces/gfpaReport.interface'
import { FactorContrib, FactorLensEndSecurity, FactorPortContrib, HoldingsSecurityDetails } from '@/interfaces/raDashboard.interface'
import { convertNumericDateToDate, nextWeekday } from '@/utils/dateUtils'
import axiosInstance from "@/utils/interceptor"
import { parseApiError } from '@/utils/parseApiError'
import dayjs from 'dayjs'

// Items requested from the gfpa/security-details endpoint: the identifier (StandardID) and issuer
// (Description) come back in the same response, so no separate security lookup is needed.
const SECURITY_CONTRIB_ITEMS = [
  ReportItem.StandardID,
  ReportItem.Description,
  // Security details (same set as the risk holdings grid).
  ReportItem.MarketValue,
  ReportItem.Holdings,
  ReportItem.SectorName,
  ReportItem.CountryCode,
  ReportItem.Currency,
  ReportItem.Price,
  ReportItem.PctFaceMarketValue,
  ReportItem.NotionalValue,
  ReportItem.PctMarketValue,
  ReportItem.SecurityCount,
  ReportItem.AssetType,
  ReportItem.AssetClass,
  ReportItem.CountryofIncorp,
  ReportItem.CountryofRisk,
  ReportItem.BaseCurrencyName,
  ReportItem.CUSIP9,
  ReportItem.ExchangeTicker,
  ReportItem.ISIN12,
  ReportItem.SEDOL7,
  // Equity attributes.
  ReportItem.DividendYield,
  ReportItem.Beta,
  ReportItem.BookToPrice,
  ReportItem.EPRatio,
  ReportItem.EarningsPerShare,
  ReportItem.MarketCap,
  ReportItem.MarketCapLog,
  ReportItem.Momentum,
  ReportItem.PBRatio,
  ReportItem.PERatio,
  ReportItem.TrailingEPS,
  ReportItem.Volatility,
  ReportItem.MSCICountry,
  ReportItem.GICSIndustryGrp,
  ReportItem.GICSIndustry,
  ReportItem.GICSSector,
  ReportItem.GICSSubIndustry,
  ReportItem.ReturnOnEquity,
  // Fixed income attributes.
  ReportItem.CouponRate,
  ReportItem.MaturityDate,
  ReportItem.MarketValue,
  ReportItem.EffectiveDuration,
  ReportItem.MoodyQuality,
  ReportItem.YearsToMaturity,
  ReportItem.OAS,
  ReportItem.DV01,
  ReportItem.Convexity,
  ReportItem.YieldToMaturity,
  ReportItem.SpreadDuration,
  ReportItem.YieldToWorst,
  ReportItem.AverageLife,
  ReportItem.BookValue,
  ReportItem.CashFlowYield,
  ReportItem.CurrentYield,
  ReportItem.EffectiveD2,
  ReportItem.EffectiveD3,
  ReportItem.DurContributionPct,
  ReportItem.DurContributionYrs,
  ReportItem.EffectiveSpreadDuration,
  ReportItem.SpreadDurContributionPct,
  ReportItem.SpreadDurContributionYrs,
  ReportItem.GainLoss,
  ReportItem.MacaulayDuration,
  ReportItem.MarketValuePar,
  ReportItem.ModifiedDuration,
  ReportItem.SPQuality,
  ReportItem.BaseTotalAccruedInterest,
  ReportItem.YearsToWorst,
  ReportItem.DatedDate,
  ReportItem.KeyRate1Month,
  ReportItem.KeyRate1Year,
  ReportItem.KeyRate2Year,
  ReportItem.KeyRate3Month,
  ReportItem.KeyRate3Year,
  ReportItem.KeyRate4Year,
  ReportItem.KeyRate5Year,
  ReportItem.KeyRate6Month,
  ReportItem.KeyRate6Year,
  ReportItem.KeyRate7Year,
  ReportItem.KeyRate8Year,
  ReportItem.KeyRate9Month,
  ReportItem.KeyRate9Year,
  ReportItem.KeyRate10Year,
  ReportItem.KeyRate15Year,
  ReportItem.KeyRate0Year,
  ReportItem.KeyRate25Year,
  ReportItem.KeyRate30Year,
  'security_ret_contrib',
  'security_ret',
  'model_ret',
  'fi_yield_ret_contrib',
  'fi_d1_ret_contrib',
  'fi_sector_ret_contrib',
  'fi_quality_ret_contrib',
  'fi_other_ret_contrib',
  'eq_country_ret_contrib',
  'eq_region_ret_contrib',
  'eq_industry_ret_contrib',
  'eq_fundamental_ret_contrib',
  'commodity_ret_contrib',
  'alternatives_ret_contrib',
  'model_ret_contrib',
  'currency_ret_contrib',
  'hedge_cost_contrib',
  'interaction_contrib',
  'selection_contrib',
]

export type ReportDataCacheKey = "factorLens"
  | "endContribPortfolio"
  | "portfolioReportItems"
  | "riskMetricsSummary"
  | "holdingsSecurityDetails"
  | "riskOneYearHistory";

export type AttributionDataCacheKey = "totalReturn" | "attrDurations" | "dailySummaries" | "factorContributionFreq" | "factorContributionTotal" | "securityContribution";

export interface ReportDataCache {
  "reportId": number
  "factorLens": FactorLensResult | null
  "endContribPortfolio": FactorLensEndContribResult | null
  "portfolioReportItems": Record<ReportTotalItem, ReportItemData | null>,
  "riskMetricsSummary": RiskMetricsSummaryData | null,
  "holdingsSecurityDetails": HoldingsSecurityDetails[] | null,
  "riskOneYearHistory": RiskHistoryData | null
}
export interface AttributionDataCache {
  "cacheKey": string
  "totalReturn": AttributionTotalReturnsData | null
  "attrDurations": AttributionDurationData | null
  "dailySummaries": Record<string, AttributionGfpaSummaryDatum> | null
}

export interface AttributionTotalReturnsData {
  portfolioContribution: number
  benchmarkContribution: number
  differenceContribution: number
}

export interface AttributionTotalReturnsResult {
  "total_ret_contrib_p": number,
  "total_ret_contrib_b": number,
  "total_ret_contrib_d": number
}

export interface AttributionDurationDataSet {
  effective: number,
  spread: number
}

export interface AttributionDurationData {
  portfolio: AttributionDurationDataSet,
  benchmark: AttributionDurationDataSet
}

export interface AttributionDurationResult {
  "edur_p": number,
  "edur_b": number,
  "sdur_p": number,
  "sdur_b": number
}

export interface AttributionGfpaSummaryDatum {
  date: Date,
  reportId: number | null,
  portfolioReturn: number,
  benchmarkReturn: number,
  activeReturn: number,
  portfolioExposure: number | null,
  benchmarkExposure: number | null
}

export interface AttributionGfpaSummaryResult {
  "date_col": string,
  "p": number,
  "b": number,
  "report_id"?: number | null,
  "report_begin_time"?: string | null,
  "report_begin_date"?: string | null,
  "report_end_date"?: string | null,
  "d": number,
  "portfolio_weighted_exposure"?: number | null,
  "benchmark_weighted_exposure"?: number | null
}

export interface ReportAPIResult {
  "resultKey": ReportDataCacheKey | AttributionDataCacheKey,
  "fromCache": boolean,
  "empty": boolean,
  "error"?: string | null,
  "errorCode"?: number | null
}

export interface AttributionAPIResult<T> {
  "resultKey": AttributionDataCacheKey,
  "fromCache": boolean,
  "data": T | null,
  "empty": boolean,
  "error"?: string | null,
  "errorCode"?: number | null
}

export interface ReportItemNameResult {
  "item_name": ReportTotalItem,
  "item_value": number | null,
  "item_value_string": string | null,
  "date": number | null,
  "pct_change": number | null,
  "rating_change": string | null
}

export interface ReportItemData {
  value: number | null,
  valueString: string | null,
  asOfDate: number | null,
  delta: number | null,
  pctChange: number | null,
  ratingChange: string | null
}

export interface ReportValueWithDelta {
  value: number,
  delta: number | null
}

export interface FactorLensResult {
  portfolio: FactorLensDataSet
  benchmark: FactorLensDataSet
  active: FactorLensDataSet
  portfolioSecurities: FactorPortContrib[]
}

export interface FactorLevelGroup {
  name: string,
  riskContrib: number
}

export interface FactorLensDataSet {
    factors: FactorContrib[],
    levels: {
      0: FactorLevelGroup[], // "Total Risk", not explicitly defined by api
      1: FactorLevelGroup[],
      2: FactorLevelGroup[],
      3: FactorLevelGroup[],
      4: FactorLevelGroup[],
      5: FactorLevelGroup[],
    }
}

interface FactorLensEndContribResult {
  "endCount": number
  "topContribs": FactorLensEndSecurity[]
  "botContribs": FactorLensEndSecurity[]
}

interface RiskMetricsSummaryResult {
  summary: Record<RiskMetricId, number> & { analysis_date: number },
  change?: (Record<RiskMetricId, number> & { analysis_date: number }) | null,
  prev?: null | {
    "analysis_date": number,
    "total_risk": number,
    "total_mkval": number
  }
}

export interface RiskMetricsSummaryData {
  "analysisDate": Date,
  [RiskMetricId.TotalRiskPortfolio]: ReportValueWithDelta,
  [RiskMetricId.TotalRiskBenchmark]: ReportValueWithDelta,
  [RiskMetricId.TotalRiskActive]: ReportValueWithDelta,
  "previousReportData": null | {
    "analysisDate": Date,
    "portfolioRisk": number | null,
    "marketValue": number | null
  }
}

export interface HoldingsSecurity  {
  "holdings": number,
  "description": string,
  "coupon": number,
  "moody": string,
  "price": number,
  "yte": number,
  "security_id": string,
  "maturity_date": Date,
  "sectornm": string,
  "country_issue_code": string,
  "currency_code1": string,
  "edur1": number,
  "notional_weight": number,
  "predicted_beta": number,
  "security_risk": number,
  "model_risk": number,
  "specific_risk": number,
  "fi_d1_risk_contrib": number,
  "fi_term_structure_risk_contrib": number,
  "fi_sector_risk_contrib": number,
  "fi_quality_risk_contrib": number,
  "fi_other_risk_contrib": number,
  "model_risk_contrib": number,
  "currency_risk_contrib": number,
  "specific_risk_contrib": number,
  "security_risk_contrib": number
}
//"fpmkval", "coup", "price", "mkval", "edur", "sectornm", "countrynm", "currency", "yrtomatu", "spd", "DV01", "convex", "YLD"
export interface SecurityDetailResult {
  "security_id": string,
  "description": string | null,
  "holdings": number | null,
  "notional_weight": number | null,
  "pholpar"?: number | null,
  [GfrItem.SpecificRisk]: number | null,
  [GfrItem.SecurityRisk]: number | null,
  [GfrItem.SecurityRiskContrib]: number | null,
  [GfrItem.ModelRiskContrib]: number | null,
  [GfrItem.CurrencyRiskContrib]: number | null,
  [GfrItem.CountryRiskContrib]?: number | null
  [GfrItem.FundamentalRiskContrib]?: number | null
  [GfrItem.IndustryRiskContrib]?: number | null
  [GfrItem.RegionRiskContrib]?: number | null
  [GfrItem.OtherSpreadRiskContrib]?: number | null
  [GfrItem.QualityRiskContrib]?: number | null
  [GfrItem.SectorRiskContrib]?: number | null
  [GfrItem.YieldCurveRiskContrib]?: number | null
  [GfrItem.AltRiskContrib]?: number | null
  [GfrItem.CommodityRiskContrib]?: number | null
  [GfrItem.SpecificRiskContrib]?: number | null
  [GfrItem.ModelRisk]?: number | null
  [ReportItem.CouponRate]?: number | null,
  [ReportItem.Price]?: number | null,
  [ReportItem.MarketValue]?: number | null,
  [ReportItem.EffectiveDuration]?: number | null,
  [ReportItem.SectorName]?: string | null,
  [ReportItem.CountryName]?: string | null,
  [ReportItem.Currency]?: string | null,
  [ReportItem.DV01]?: number | null,
  [ReportItem.Convexity]?: number | null,
  [ReportItem.YearsToMaturity]?: number | null,
  [ReportItem.YieldToMaturity]?: number | null,
  [ReportItem.MaturityDate]?: string | null,
  [ReportItem.OAS]?: number | null,
  [ReportItem.MoodyQuality]?: string | null,
  [ReportItem.SpreadDuration]?: number | null
  [ReportItem.YieldToWorst]?: number | null
  [ReportItem.DividendYield]?: number | null
  [ReportItem.AverageLife]?: number | null
  [ReportItem.Beta]?: number | null
  [ReportItem.BookToPrice]?: number | null
  [ReportItem.BookValue]?: number | null
  [ReportItem.CashFlowYield]?: number | null
  [ReportItem.CurrentYield]?: number | null
  [ReportItem.EPRatio]?: number | null
  [ReportItem.EarningsPerShare]?: number | null
  [ReportItem.EffectiveD2]?: number | null
  [ReportItem.EffectiveD3]?: number | null
  [ReportItem.DurContributionPct]?: number | null
  [ReportItem.DurContributionYrs]?: number | null
  [ReportItem.EffectiveSpreadDuration]?: number | null
  [ReportItem.SpreadDurContributionPct]?: number | null
  [ReportItem.SpreadDurContributionYrs]?: number | null
  [ReportItem.GainLoss]?: number | null
  [ReportItem.MacaulayDuration]?: number | null
  [ReportItem.MarketCap]?: number | null
  [ReportItem.MarketCapLog]?: number | null
  [ReportItem.MarketValuePar]?: number | null
  [ReportItem.ModifiedDuration]?: number | null
  [ReportItem.Momentum]?: number | null
  [ReportItem.NotionalValue]?: number | null
  [ReportItem.PBRatio]?: number | null
  [ReportItem.PERatio]?: number | null
  [ReportItem.PctMarketValue]?: number | null
  [ReportItem.SPQuality]?: string | null
  [ReportItem.SecurityCount]?: string | null
  [ReportItem.BaseTotalAccruedInterest]?: number | null
  [ReportItem.TrailingEPS]?: number | null
  [ReportItem.Volatility]?: number | null
  [ReportItem.YearsToWorst]?: number | null
  [ReportItem.MSCICountry]?: string | null
  [ReportItem.GICSIndustryGrp]?: string | null
  [ReportItem.GICSIndustry]?: string | null
  [ReportItem.GICSSector]?: string | null
  [ReportItem.GICSSubIndustry]?: string | null
  [ReportItem.ReturnOnEquity]?: string | null
  [ReportItem.DatedDate]?: string | null
  [ReportItem.KeyRate1Month]?: string | null
  [ReportItem.KeyRate1Year]?: string | null
  [ReportItem.KeyRate2Year]?: string | null
  [ReportItem.KeyRate3Month]?: string | null
  [ReportItem.KeyRate3Year]?: string | null
  [ReportItem.KeyRate4Year]?: string | null
  [ReportItem.KeyRate5Year]?: string | null
  [ReportItem.KeyRate6Month]?: string | null
  [ReportItem.KeyRate6Year]?: string | null
  [ReportItem.KeyRate7Year]?: string | null
  [ReportItem.KeyRate8Year]?: string | null
  [ReportItem.KeyRate9Month]?: string | null
  [ReportItem.KeyRate9Year]?: string | null
  [ReportItem.KeyRate10Year]?: string | null
  [ReportItem.KeyRate15Year]?: string | null
  [ReportItem.KeyRate0Year]?: string | null
  [ReportItem.KeyRate25Year]?: string | null
  [ReportItem.KeyRate30Year]?: string | null
  [ReportItem.AssetType]?: string | null
  [ReportItem.AssetClass]?: string | null
  [ReportItem.CountryofIncorp]?: string | null
  [ReportItem.CountryofRisk]?: string | null
  [ReportItem.BaseCurrencyName]?: string | null
  [ReportItem.CUSIP9]?: string | null
  [ReportItem.ExchangeTicker]?: string | null
  [ReportItem.ISIN12]?: string | null
  [ReportItem.SEDOL7]?: string | null
}
const SecurityDetailReportItems: ReportItem[] = [
  ReportItem.CouponRate,
  ReportItem.Price,
  ReportItem.MarketValue,
  ReportItem.EffectiveDuration,
  ReportItem.SectorName,
  ReportItem.CountryName,
  ReportItem.Currency,
  ReportItem.DV01,
  ReportItem.Convexity,
  ReportItem.YieldToMaturity,
  ReportItem.YearsToMaturity,
  ReportItem.MaturityDate,
  ReportItem.OAS,
  ReportItem.MoodyQuality,
  ReportItem.SpreadDuration,
  ReportItem.YieldToWorst,
  ReportItem.DividendYield,
  ReportItem.AverageLife,
  ReportItem.Beta,
  ReportItem.BookToPrice,
  ReportItem.BookValue,
  ReportItem.CashFlowYield,
  ReportItem.CurrentYield,
  ReportItem.EPRatio,
  ReportItem.EarningsPerShare,
  ReportItem.EffectiveD2,
  ReportItem.EffectiveD3,
  ReportItem.DurContributionPct,
  ReportItem.DurContributionYrs,
  ReportItem.EffectiveSpreadDuration,
  ReportItem.SpreadDurContributionPct,
  ReportItem.SpreadDurContributionYrs,
  ReportItem.GainLoss,
  ReportItem.MacaulayDuration,
  ReportItem.MarketCap,
  ReportItem.MarketCapLog,
  ReportItem.MarketValuePar,
  ReportItem.ModifiedDuration,
  ReportItem.Momentum,
  ReportItem.NotionalValue,
  ReportItem.PBRatio,
  ReportItem.PERatio,
  ReportItem.PctMarketValue,
  ReportItem.SPQuality,
  ReportItem.SecurityCount,
  ReportItem.BaseTotalAccruedInterest,
  ReportItem.TrailingEPS,
  ReportItem.Volatility,
  ReportItem.YearsToWorst,
  ReportItem.MSCICountry,
  ReportItem.GICSIndustryGrp,
  ReportItem.GICSIndustry,
  ReportItem.GICSSector,
  ReportItem.GICSSubIndustry,
  ReportItem.ReturnOnEquity,
  ReportItem.DatedDate,
  ReportItem.KeyRate1Month,
  ReportItem.KeyRate1Year,
  ReportItem.KeyRate2Year,
  ReportItem.KeyRate3Month,
  ReportItem.KeyRate3Year,
  ReportItem.KeyRate4Year,
  ReportItem.KeyRate5Year,
  ReportItem.KeyRate6Month,
  ReportItem.KeyRate6Year,
  ReportItem.KeyRate7Year,
  ReportItem.KeyRate8Year,
  ReportItem.KeyRate9Month,
  ReportItem.KeyRate9Year,
  ReportItem.KeyRate10Year,
  ReportItem.KeyRate15Year,
  ReportItem.KeyRate0Year,
  ReportItem.KeyRate25Year,
  ReportItem.KeyRate30Year,
  ReportItem.AssetType,
  ReportItem.AssetClass,
  ReportItem.CountryofIncorp,
  ReportItem.CountryofRisk,
  ReportItem.BaseCurrencyName,
  ReportItem.CUSIP9,
  ReportItem.ExchangeTicker,
  ReportItem.ISIN12,
  ReportItem.SEDOL7
];
const SecurityDetailGfrItems: GfrItem[] = [
  GfrItem.CountryRiskContrib,
  GfrItem.FundamentalRiskContrib,
  GfrItem.IndustryRiskContrib,
  GfrItem.RegionRiskContrib,
  GfrItem.OtherSpreadRiskContrib,
  GfrItem.QualityRiskContrib,
  GfrItem.SectorRiskContrib,
  GfrItem.YieldCurveRiskContrib,
  GfrItem.AltRiskContrib,
  GfrItem.CommodityRiskContrib,
  GfrItem.SpecificRiskContrib,
  GfrItem.ModelRiskContrib,
  GfrItem.CurrencyRiskContrib,
  GfrItem.SecurityRiskContrib,
  GfrItem.SecurityRisk,
  GfrItem.ModelRisk,
  GfrItem.SpecificRisk
]

interface RiskHistoryResultDatum {
  "portfolioTotalRisk": number,
  "benchmarkTotalRisk": number,
  "activeTotalRisk": number,
  "analysisDate": string,
  "period_var": number
}

export interface RiskHistoryData {
  latestDateAvailable: Date,
  earliestDateAvailable: Date,
  historicalData: RiskHistoryDateStats[]
}

export interface RiskHistoryDateStats {
  "portfolioTotalRisk": number,
  "benchmarkTotalRisk": number,
  "activeTotalRisk": number,
  "analysisDate": Date,
  "period_var": number
}

class RADashboardDataService {
  private baseUrl = "/reportDashboard/api";
  private reportId : number | null = null;
  private riskCaches : Record<number, ReportDataCache> = {};
  private attrCaches : Record<string, AttributionDataCache> = {};
  // Own bounded LRU cache: zoom reaches arbitrary ranges, so keep them out of attrCaches.
  private static readonly FACTOR_FREQ_CACHE_LIMIT = 10;
  private factorFreqCache : Map<string, IGfpaFactorContributionFreq[]> = new Map();
  // Same bounded LRU for the no-frequency factor totals (drives the strip/table over the active range).
  private static readonly FACTOR_TOTAL_CACHE_LIMIT = 10;
  private factorTotalCache : Map<string, IGfpaFactorContributionTotal[]> = new Map();
  // Bounded LRU for the per-security holdings rows — a full array per date range, so keep it out of attrCaches.
  private static readonly SECURITY_CONTRIB_CACHE_LIMIT = 10;
  private securityContribCache : Map<string, IGfpaSecurityContrib[]> = new Map();
  // Bounded LRU for zoom-scoped total returns — arbitrary ranges, so keep them out of attrCaches.
  private static readonly TOTAL_RETURN_CACHE_LIMIT = 10;
  private totalReturnZoomCache : Map<string, AttributionTotalReturnsData> = new Map();
  private async getAxiosResponse(endpoint: string, queryParams: Record<string, any> = {}) {
    const reportId = this.reportId;
    const response = await axiosInstance.get(`${this.baseUrl}/${endpoint}`, {
      params: {
        reportId,
        ...queryParams
      }
    });
    return response ?? null;
  }
  private async postAxiosResponse(endpoint: string, bodyData: Record<string, any>) {
    const reportId = this.reportId;
    const response = await axiosInstance.post(`${this.baseUrl}/${endpoint}`, {
      reportId,
      ...bodyData
    });
    return response ?? null;
  }
  static newCache(reportId: number): ReportDataCache {
    return {
      reportId,
      factorLens: null,
      endContribPortfolio: null,
      riskMetricsSummary: null,
      holdingsSecurityDetails: null,
      riskOneYearHistory: null,
      portfolioReportItems: Object.fromEntries(
        AllReportTotalItems.map(item => [item, null])
      ) as Record<ReportTotalItem, ReportItemData | null>
    }
  }
  static newAttrCache(cacheKey: string): AttributionDataCache {
    return {
      cacheKey,
      totalReturn: null,
      attrDurations: null,
      dailySummaries: null
    }
  }
  private getCache() {
    if (this.reportId != null && this.reportId in this.riskCaches) {
      return this.riskCaches[this.reportId];
    }
    const cache: ReportDataCache = RADashboardDataService.newCache(this.reportId ?? 0);
    if (this.reportId != null) {
      this.riskCaches[this.reportId] = cache;
    }
    return cache;
  }
  private getAttrCache(cacheKey: string) {
    if (cacheKey in this.attrCaches) {
      return this.attrCaches[cacheKey];
    }
    const cache = RADashboardDataService.newAttrCache(cacheKey);
    this.attrCaches[cacheKey] = cache;
    return cache;
  }
  // Read an LRU cache, marking the hit as most-recently-used (re-insert moves it to the newest slot).
  private static lruGet<T>(cache: Map<string, T>, cacheKey: string): T | undefined {
    const hit = cache.get(cacheKey);
    if (hit === undefined) return undefined;
    cache.delete(cacheKey);
    cache.set(cacheKey, hit);
    return hit;
  }
  // Store into an LRU cache, evicting the least-recently-used (oldest key) once over the cap.
  private static lruSet<T>(cache: Map<string, T>, cacheKey: string, value: T, limit: number) {
    cache.delete(cacheKey);
    cache.set(cacheKey, value);
    if (cache.size > limit) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) cache.delete(oldest);
    }
  }
  setReportId(reportId: number) {
    this.reportId = reportId;
  }
  clearReportId() {
    this.reportId = null;
  }
  getReportData(reportId: number | null = null) {
    if (reportId == null) {
      if (this.reportId == null) return null;
      reportId = this.reportId;
    }
    return this.riskCaches[reportId] || null;
  }
  getAttributionData(beginDate: dayjs.Dayjs, endDate: dayjs.Dayjs, reportId: number | null = null): AttributionDataCache | null {
    if (reportId == null) {
      if (this.reportId == null) return null;
      reportId = this.reportId;
    }
    const cacheKey = RADashboardDataService.makeAttributionCacheKey(reportId, beginDate, endDate);
    const rangeCache = this.attrCaches[cacheKey];
    const summaryCache = this.attrCaches[reportId];
    if (rangeCache == null && summaryCache == null) {
      return null;
    } else if (rangeCache == null) {
      return summaryCache;
    }
    return {
      ...rangeCache,
      dailySummaries: summaryCache.dailySummaries
    }
  }
  async getAllPortfolioReportItems() {
    return await this.getPortfolioReportItems(...AllReportTotalItems);
  }
  async getPortfolioReportItems(...items: ReportTotalItem[]) {
    const apiCallId = "portfolioReportItems";
    if (this.reportId === null) return null;
    let result: ReportAPIResult = {
      resultKey: apiCallId,
      fromCache: false,
      empty: false
    }
    const cache = this.getCache();
    let cachePull = cache[apiCallId]; // Cache Pull will never be null here because it holds another record.
    // If every item has a cache value pulled already
    if (items.every(item => cachePull[item] != null)) {
      result.fromCache = true;
    }
    if (result.fromCache) return result;
    cachePull = {
      ...cachePull
    };
    try {
      const response = await this.getAxiosResponse("v1/gfr/getPortTotalItemValues", {
        itemNames: items,
        dataSet: ReportDataSet.Portfolio
      });
      if (!response?.data) {
        result.empty = true;
        return result;
      }
      const data: ReportItemNameResult[] = response.data;
      data.forEach(res => {
        if (cachePull[res.item_name] === null && (res.item_value !== null || res.item_value_string !== null)) {
          cachePull[res.item_name] = {
            value: res.item_value,
            valueString: res.item_value_string ?? null,
            asOfDate: res.date ?? null,
            delta: null,
            pctChange: res.pct_change ?? null,
            ratingChange: res.rating_change ?? null
          };
        }
      });
      if (!data.length) result.empty = true;
      cache[apiCallId] = cachePull;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }
  async getFactorLens(assetClass?: string | null) {
    const apiCallId = "factorLens";
    if (this.reportId === null) return null;
    let result: ReportAPIResult = {
      resultKey: apiCallId,
      fromCache: false,
      empty: false
    };
    const cache = this.getCache();
    const cachePull = cache[apiCallId];
    if (cachePull !== null) {
      result.fromCache = true;
      return result;
    }
    try {
      const response = await this.getAxiosResponse('v1/gfr/getFactorLens', {
        assetClass
      });
      if (response === null) {
        result.empty = true;
        result.error = "Null Response";
        return result;
      }
      const data: FactorContrib[] = response.data;
      const parsedData = RADashboardDataService.sortFactorContribData(data);
      const portSecuritiesResponse = await this.getAxiosResponse(`v1/gfr/getFactorPortSecurityContrib`, {
        assetClass
      });
      const portData: FactorPortContrib[] = Array.isArray(portSecuritiesResponse?.data)
        ? portSecuritiesResponse.data
        : [];
      parsedData.portfolioSecurities = portData;
      // If we want the cache to succeed even if the second request fails, move this line before the second request.
      cache[apiCallId] = parsedData;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }
  async getPortfolioEndSecurities() {
    const apiCallId = "endContribPortfolio";
    if (this.reportId === null) return null;
    let result: ReportAPIResult = {
      resultKey: apiCallId,
      fromCache: false,
      empty: false
    };
    const cache = this.getCache();
    const cachePull = cache[apiCallId];
    if (cachePull !== null) {
      result.fromCache = true;
      return result;
    }
    try {
      const limitCount = 5;
      const response = await this.getAxiosResponse("v1/gfr/getTopBottomSecurities", {
        dataSet: "P",
        limitCount
      });
      if (response === null) {
        result.empty = true;
        result.error = "Null Response";
        return result;
      }
      const data: FactorLensEndSecurity[] = response.data;
      let resultData : FactorLensEndContribResult = {
        endCount: limitCount,
        topContribs: [],
        botContribs: []
      }
      if (!data.length) {
        result.empty = true;
      }
      data.forEach(contrib => {
        if (contrib.rank_type === "Top") resultData.topContribs.push(contrib);
        else if (contrib.rank_type === "Bottom") resultData.botContribs.push(contrib);
      });
      cache[apiCallId] = resultData;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }
  async getRiskMetricsSummary() {
    const apiCallId = "riskMetricsSummary";
    if (this.reportId === null) return null;
    let result: ReportAPIResult = {
      resultKey: apiCallId,
      fromCache: false,
      empty: false
    };
    const cache = this.getCache();
    const cachePull = cache[apiCallId];
    if (cachePull !== null) {
      result.fromCache = true;
      return result;
    }
    try {
      const response = await this.getAxiosResponse("v1/gfr/getRiskSummary");
      if (response === null || response.data == null) {
        result.empty = true;
        result.error = "Data not found";
        return result;
      }
      const data: RiskMetricsSummaryResult = response.data;
      if (data.summary == null) {
        result.empty = true;
        return result;
      }
      const parsedData: RiskMetricsSummaryData = {
        analysisDate: convertNumericDateToDate(data.summary.analysis_date),
        [RiskMetricId.TotalRiskPortfolio]: {
          value: data.summary[RiskMetricId.TotalRiskPortfolio],
          delta: data.change?.[RiskMetricId.TotalRiskPortfolio] ?? null
        },
        [RiskMetricId.TotalRiskBenchmark]: {
          value: data.summary[RiskMetricId.TotalRiskBenchmark],
          delta: data.change?.[RiskMetricId.TotalRiskBenchmark] ?? null
        },
        [RiskMetricId.TotalRiskActive]: {
          value: data.summary[RiskMetricId.TotalRiskActive],
          delta: data.change?.[RiskMetricId.TotalRiskActive] ?? null
        },
        "previousReportData": null
      };
      if (data.prev) {
        const prev = data.prev;
        parsedData.previousReportData = {
          analysisDate: convertNumericDateToDate(prev.analysis_date),
          portfolioRisk: prev.total_risk,
          marketValue: prev.total_mkval
        }
      }
      cache[apiCallId] = parsedData;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }
  async getSecurityDetails() {
    const apiCallId = "holdingsSecurityDetails";
    if (this.reportId === null) return null;
    let result: ReportAPIResult = {
      resultKey: apiCallId,
      fromCache: false,
      empty: false
    };
    const cache = this.getCache();
    const cachePull = cache[apiCallId];
    if (cachePull !== null) {
      result.fromCache = true;
      return result;
    }
    try {
      const apiItems = [...SecurityDetailReportItems, ...SecurityDetailGfrItems];
      const batchedData: SecurityDetailResult[][] = [];
      let batchedItems = apiItems.splice(0, 30);
      let loopCount = 0;
      while (batchedItems.length && loopCount < 10) {
        const response = await this.postAxiosResponse("v2/gfr/getSecurityDetail", {
          items: batchedItems
        });
        if (response === null || !response.data) {
          result.empty = true;
          result.error = "Data not found";
          console.log("Data Missing");
          return result;
        } else if (!response.data?.length) {
          result.empty = true;
          result.error = "Data Empty";
          console.log("Data Empty");
          return result;
        }
        batchedItems = apiItems.splice(0, 30);
        batchedData.push(response.data);
      }
      const parsedBatches: HoldingsSecurityDetails[] = [];
      batchedData[0].forEach((secDatum, i) => {
        let detail: HoldingsSecurityDetails = {
          identifier: secDatum.security_id,
          holdings: secDatum.holdings,
          issuer: secDatum.description,
          securityRisk: secDatum[GfrItem.SecurityRisk],
          securityRiskContrib: secDatum[GfrItem.SecurityRiskContrib],
          factorRiskContrib: secDatum[GfrItem.ModelRiskContrib], // model risk contrib?
          notionalWeight: secDatum.notional_weight,
          parWeight: 0,
          coupon: null,
          maturity: null, // Calculated from Years To Maturity?
          price: null, // price
          marketValue: null, // mkval
          marketValueMillions: null,
          duration: null,
          sector: null, // sectornm
          country: null, // countrynm
          currency: null, // currency
          rating: null,
          yearsToMaturity: null, // yrtomatu
          spread: null, // spd
          dv01: null,
          convexity: null,
          yieldToMaturity: null,
          [GfrItem.CountryRiskContrib]: null,
          [GfrItem.FundamentalRiskContrib]: null,
          [GfrItem.IndustryRiskContrib]: null,
          [GfrItem.RegionRiskContrib]: null,
          [GfrItem.OtherSpreadRiskContrib]: null,
          [GfrItem.QualityRiskContrib]: null,
          [GfrItem.SectorRiskContrib]: null,
          [GfrItem.YieldCurveRiskContrib]: null,
          [GfrItem.AltRiskContrib]: null,
          [GfrItem.CommodityRiskContrib]: null,
          [GfrItem.SpecificRiskContrib]: null,
          [GfrItem.ModelRiskContrib]: secDatum[GfrItem.ModelRiskContrib],
          [GfrItem.CurrencyRiskContrib]: null,
          [GfrItem.SecurityRiskContrib]: secDatum[GfrItem.SecurityRiskContrib],
          [GfrItem.SecurityRisk]: secDatum[GfrItem.SecurityRisk],
          [GfrItem.ModelRisk]: null,
          [GfrItem.SpecificRisk]: null,
          [ReportItem.SpreadDuration]: null,
          [ReportItem.YieldToWorst]: null,
          [ReportItem.DividendYield]: null,
          [ReportItem.AverageLife]: null,
          [ReportItem.Beta]: null,
          [ReportItem.BookToPrice]: null,
          [ReportItem.BookValue]: null,
          [ReportItem.CashFlowYield]: null,
          [ReportItem.CurrentYield]: null,
          [ReportItem.EPRatio]: null,
          [ReportItem.EarningsPerShare]: null,
          [ReportItem.EffectiveD2]: null,
          [ReportItem.EffectiveD3]: null,
          [ReportItem.DurContributionPct]: null,
          [ReportItem.DurContributionYrs]: null,
          [ReportItem.EffectiveSpreadDuration]: null,
          [ReportItem.SpreadDurContributionPct]: null,
          [ReportItem.SpreadDurContributionYrs]: null,
          [ReportItem.GainLoss]: null,
          [ReportItem.MacaulayDuration]: null,
          [ReportItem.MarketCap]: null,
          [ReportItem.MarketCapLog]: null,
          [ReportItem.MarketValuePar]: null,
          [ReportItem.ModifiedDuration]: null,
          [ReportItem.Momentum]: null,
          [ReportItem.NotionalValue]: null,
          [ReportItem.PBRatio]: null,
          [ReportItem.PERatio]: null,
          [ReportItem.PctMarketValue]: null,
          [ReportItem.SPQuality]: null,
          [ReportItem.SecurityCount]: null,
          [ReportItem.BaseTotalAccruedInterest]: null,
          [ReportItem.TrailingEPS]: null,
          [ReportItem.Volatility]: null,
          [ReportItem.YearsToWorst]: null,
          [ReportItem.MSCICountry]: null,
          [ReportItem.GICSIndustryGrp]: null,
          [ReportItem.GICSIndustry]: null,
          [ReportItem.GICSSector]: null,
          [ReportItem.GICSSubIndustry]: null,
          [ReportItem.ReturnOnEquity]: null,
          [ReportItem.DatedDate]: null,
          [ReportItem.KeyRate1Month]: null,
          [ReportItem.KeyRate1Year]: null,
          [ReportItem.KeyRate2Year]: null,
          [ReportItem.KeyRate3Month]: null,
          [ReportItem.KeyRate3Year]: null,
          [ReportItem.KeyRate4Year]: null,
          [ReportItem.KeyRate5Year]: null,
          [ReportItem.KeyRate6Month]: null,
          [ReportItem.KeyRate6Year]: null,
          [ReportItem.KeyRate7Year]: null,
          [ReportItem.KeyRate8Year]: null,
          [ReportItem.KeyRate9Month]: null,
          [ReportItem.KeyRate9Year]: null,
          [ReportItem.KeyRate10Year]: null,
          [ReportItem.KeyRate15Year]: null,
          [ReportItem.KeyRate0Year]: null,
          [ReportItem.KeyRate25Year]: null,
          [ReportItem.KeyRate30Year]: null,
          [ReportItem.AssetType]: null,
          [ReportItem.AssetClass]: null,
          [ReportItem.CountryofIncorp]: null,
          [ReportItem.CountryofRisk]: null,
          [ReportItem.BaseCurrencyName]: null,
          [ReportItem.CUSIP9]: null,
          [ReportItem.ExchangeTicker]: null,
          [ReportItem.ISIN12]: null,
          [ReportItem.SEDOL7]: null
        }
        const combinedDetail = batchedData.reduce((prevDetail, batch) => {
          const datum = batch[i];
          let mkval = datum[ReportItem.MarketValue] ?? null;
          let nextDetail: HoldingsSecurityDetails = {
            identifier: datum.security_id,
            holdings: datum.holdings,
            issuer: datum.description,
            securityRisk: datum[GfrItem.SecurityRisk],
            securityRiskContrib: datum[GfrItem.SecurityRiskContrib],
            factorRiskContrib: datum[GfrItem.ModelRiskContrib], // model risk contrib?
            notionalWeight: datum.notional_weight,
            parWeight: 0,
            coupon: prevDetail.coupon ?? datum[ReportItem.CouponRate] ?? null,
            maturity: prevDetail.maturity ?? datum[ReportItem.MaturityDate] ?? null, // Calculated from Years To Maturity?
            price: prevDetail.price ?? datum[ReportItem.Price] ?? null, // price
            marketValue: prevDetail.marketValue ?? mkval, // mkval
            marketValueMillions: prevDetail.marketValueMillions ?? (mkval == null ? null : mkval / 1000000),
            duration: prevDetail.duration ?? datum[ReportItem.EffectiveDuration] ?? null,
            sector: prevDetail.sector ?? datum[ReportItem.SectorName] ?? null, // sectornm
            country: prevDetail.country ?? datum[ReportItem.CountryName] ?? null, // countrynm
            currency: prevDetail.currency ?? datum[ReportItem.Currency] ?? null, // currency
            rating: prevDetail.rating ?? datum[ReportItem.MoodyQuality] ?? null,
            yearsToMaturity: prevDetail.yearsToMaturity ?? datum[ReportItem.YearsToMaturity] ?? null, // yrtomatu
            spread: prevDetail.spread ?? datum[ReportItem.OAS] ?? null, // spd
            dv01: prevDetail.dv01 ?? datum[ReportItem.DV01] ?? null,
            convexity: prevDetail.convexity ?? datum[ReportItem.Convexity] ?? null,
            yieldToMaturity: prevDetail.yieldToMaturity ?? datum[ReportItem.YieldToMaturity] ?? null,
            [GfrItem.CountryRiskContrib]: prevDetail[GfrItem.CountryRiskContrib] ?? datum[GfrItem.CountryRiskContrib] ?? null,
            [GfrItem.FundamentalRiskContrib]: prevDetail[GfrItem.FundamentalRiskContrib] ?? datum[GfrItem.FundamentalRiskContrib] ?? null,
            [GfrItem.IndustryRiskContrib]: prevDetail[GfrItem.IndustryRiskContrib] ?? datum[GfrItem.IndustryRiskContrib] ?? null,
            [GfrItem.RegionRiskContrib]: prevDetail[GfrItem.RegionRiskContrib] ?? datum[GfrItem.RegionRiskContrib] ?? null,
            [GfrItem.OtherSpreadRiskContrib]: prevDetail[GfrItem.OtherSpreadRiskContrib] ?? datum[GfrItem.OtherSpreadRiskContrib] ?? null,
            [GfrItem.QualityRiskContrib]: prevDetail[GfrItem.QualityRiskContrib] ?? datum[GfrItem.QualityRiskContrib] ?? null,
            [GfrItem.SectorRiskContrib]: prevDetail[GfrItem.SectorRiskContrib] ?? datum[GfrItem.SectorRiskContrib] ?? null,
            [GfrItem.YieldCurveRiskContrib]: prevDetail[GfrItem.YieldCurveRiskContrib] ?? datum[GfrItem.YieldCurveRiskContrib] ?? null,
            [GfrItem.AltRiskContrib]: prevDetail[GfrItem.AltRiskContrib] ?? datum[GfrItem.AltRiskContrib] ?? null,
            [GfrItem.CommodityRiskContrib]: prevDetail[GfrItem.CommodityRiskContrib] ?? datum[GfrItem.CommodityRiskContrib] ?? null,
            [GfrItem.SpecificRiskContrib]: prevDetail[GfrItem.SpecificRiskContrib] ?? datum[GfrItem.SpecificRiskContrib] ?? null,
            [GfrItem.ModelRiskContrib]: prevDetail[GfrItem.ModelRiskContrib] ?? datum[GfrItem.ModelRiskContrib],
            [GfrItem.CurrencyRiskContrib]: prevDetail[GfrItem.CurrencyRiskContrib] ?? datum[GfrItem.CurrencyRiskContrib] ?? null,
            [GfrItem.SecurityRiskContrib]: prevDetail[GfrItem.SecurityRiskContrib] ?? datum[GfrItem.SecurityRiskContrib],
            [GfrItem.SecurityRisk]: prevDetail[GfrItem.SecurityRisk] ?? datum[GfrItem.SecurityRisk],
            [GfrItem.ModelRisk]: prevDetail[GfrItem.ModelRisk] ?? datum[GfrItem.ModelRisk] ?? null,
            [GfrItem.SpecificRisk]: prevDetail[GfrItem.SpecificRisk] ?? datum[GfrItem.SpecificRisk] ?? null,
            [ReportItem.SpreadDuration]: prevDetail[ReportItem.SpreadDuration] ?? datum[ReportItem.SpreadDuration] ?? null,
            [ReportItem.YieldToWorst]: prevDetail[ReportItem.YieldToWorst] ?? datum[ReportItem.YieldToWorst] ?? null,
            [ReportItem.DividendYield]: prevDetail[ReportItem.DividendYield] ?? datum[ReportItem.DividendYield] ?? null,
            [ReportItem.AverageLife]: prevDetail[ReportItem.AverageLife] ?? datum[ReportItem.AverageLife] ?? null,
            [ReportItem.Beta]: prevDetail[ReportItem.Beta] ?? datum[ReportItem.Beta] ?? null,
            [ReportItem.BookToPrice]: prevDetail[ReportItem.BookToPrice] ?? datum[ReportItem.BookToPrice] ?? null,
            [ReportItem.BookValue]: prevDetail[ReportItem.BookValue] ?? datum[ReportItem.BookValue] ?? null,
            [ReportItem.CashFlowYield]: prevDetail[ReportItem.CashFlowYield] ?? datum[ReportItem.CashFlowYield] ?? null,
            [ReportItem.CurrentYield]: prevDetail[ReportItem.CurrentYield] ?? datum[ReportItem.CurrentYield] ?? null,
            [ReportItem.EPRatio]: prevDetail[ReportItem.EPRatio] ?? datum[ReportItem.EPRatio] ?? null,
            [ReportItem.EarningsPerShare]: prevDetail[ReportItem.EarningsPerShare] ?? datum[ReportItem.EarningsPerShare] ?? null,
            [ReportItem.EffectiveD2]: prevDetail[ReportItem.EffectiveD2] ?? datum[ReportItem.EffectiveD2] ?? null,
            [ReportItem.EffectiveD3]: prevDetail[ReportItem.EffectiveD3] ?? datum[ReportItem.EffectiveD3] ?? null,
            [ReportItem.DurContributionPct]: prevDetail[ReportItem.DurContributionPct] ?? datum[ReportItem.DurContributionPct] ?? null,
            [ReportItem.DurContributionYrs]: prevDetail[ReportItem.DurContributionYrs] ?? datum[ReportItem.DurContributionYrs] ?? null,
            [ReportItem.EffectiveSpreadDuration]: prevDetail[ReportItem.EffectiveSpreadDuration] ?? datum[ReportItem.EffectiveSpreadDuration] ?? null,
            [ReportItem.SpreadDurContributionPct]: prevDetail[ReportItem.SpreadDurContributionPct] ?? datum[ReportItem.SpreadDurContributionPct] ?? null,
            [ReportItem.SpreadDurContributionYrs]: prevDetail[ReportItem.SpreadDurContributionYrs] ?? datum[ReportItem.SpreadDurContributionYrs] ?? null,
            [ReportItem.GainLoss]: prevDetail[ReportItem.GainLoss] ?? datum[ReportItem.GainLoss] ?? null,
            [ReportItem.MacaulayDuration]: prevDetail[ReportItem.MacaulayDuration] ?? datum[ReportItem.MacaulayDuration] ?? null,
            [ReportItem.MarketCap]: prevDetail[ReportItem.MarketCap] ?? datum[ReportItem.MarketCap] ?? null,
            [ReportItem.MarketCapLog]: prevDetail[ReportItem.MarketCapLog] ?? datum[ReportItem.MarketCapLog] ?? null,
            [ReportItem.MarketValuePar]: prevDetail[ReportItem.MarketValuePar] ?? datum[ReportItem.MarketValuePar] ?? null,
            [ReportItem.ModifiedDuration]: prevDetail[ReportItem.ModifiedDuration] ?? datum[ReportItem.ModifiedDuration] ?? null,
            [ReportItem.Momentum]: prevDetail[ReportItem.Momentum] ?? datum[ReportItem.Momentum] ?? null,
            [ReportItem.NotionalValue]: prevDetail[ReportItem.NotionalValue] ?? datum[ReportItem.NotionalValue] ?? null,
            [ReportItem.PBRatio]: prevDetail[ReportItem.PBRatio] ?? datum[ReportItem.PBRatio] ?? null,
            [ReportItem.PERatio]: prevDetail[ReportItem.PERatio] ?? datum[ReportItem.PERatio] ?? null,
            [ReportItem.PctMarketValue]: prevDetail[ReportItem.PctMarketValue] ?? datum[ReportItem.PctMarketValue] ?? null,
            [ReportItem.SPQuality]: prevDetail[ReportItem.SPQuality] ?? datum[ReportItem.SPQuality] ?? null,
            [ReportItem.SecurityCount]: prevDetail[ReportItem.SecurityCount] ?? datum[ReportItem.SecurityCount] ?? null,
            [ReportItem.BaseTotalAccruedInterest]: prevDetail[ReportItem.BaseTotalAccruedInterest] ?? datum[ReportItem.BaseTotalAccruedInterest] ?? null,
            [ReportItem.TrailingEPS]: prevDetail[ReportItem.TrailingEPS] ?? datum[ReportItem.TrailingEPS] ?? null,
            [ReportItem.Volatility]: prevDetail[ReportItem.Volatility] ?? datum[ReportItem.Volatility] ?? null,
            [ReportItem.YearsToWorst]: prevDetail[ReportItem.YearsToWorst] ?? datum[ReportItem.YearsToWorst] ?? null,
            [ReportItem.MSCICountry]: prevDetail[ReportItem.MSCICountry] ?? datum[ReportItem.MSCICountry] ?? null,
            [ReportItem.GICSIndustryGrp]: prevDetail[ReportItem.GICSIndustryGrp] ?? datum[ReportItem.GICSIndustryGrp] ?? null,
            [ReportItem.GICSIndustry]: prevDetail[ReportItem.GICSIndustry] ?? datum[ReportItem.GICSIndustry] ?? null,
            [ReportItem.GICSSector]: prevDetail[ReportItem.GICSSector] ?? datum[ReportItem.GICSSector] ?? null,
            [ReportItem.GICSSubIndustry]: prevDetail[ReportItem.GICSSubIndustry] ?? datum[ReportItem.GICSSubIndustry] ?? null,
            [ReportItem.ReturnOnEquity]: prevDetail[ReportItem.ReturnOnEquity] ?? datum[ReportItem.ReturnOnEquity] ?? null,
            [ReportItem.DatedDate]: prevDetail[ReportItem.DatedDate] ?? datum[ReportItem.DatedDate] ?? null,
            [ReportItem.KeyRate1Month]: prevDetail[ReportItem.KeyRate1Month] ?? datum[ReportItem.KeyRate1Month] ?? null,
            [ReportItem.KeyRate1Year]: prevDetail[ReportItem.KeyRate1Year] ?? datum[ReportItem.KeyRate1Year] ?? null,
            [ReportItem.KeyRate2Year]: prevDetail[ReportItem.KeyRate2Year] ?? datum[ReportItem.KeyRate2Year] ?? null,
            [ReportItem.KeyRate3Month]: prevDetail[ReportItem.KeyRate3Month] ?? datum[ReportItem.KeyRate3Month] ?? null,
            [ReportItem.KeyRate3Year]: prevDetail[ReportItem.KeyRate3Year] ?? datum[ReportItem.KeyRate3Year] ?? null,
            [ReportItem.KeyRate4Year]: prevDetail[ReportItem.KeyRate4Year] ?? datum[ReportItem.KeyRate4Year] ?? null,
            [ReportItem.KeyRate5Year]: prevDetail[ReportItem.KeyRate5Year] ?? datum[ReportItem.KeyRate5Year] ?? null,
            [ReportItem.KeyRate6Month]: prevDetail[ReportItem.KeyRate6Month] ?? datum[ReportItem.KeyRate6Month] ?? null,
            [ReportItem.KeyRate6Year]: prevDetail[ReportItem.KeyRate6Year] ?? datum[ReportItem.KeyRate6Year] ?? null,
            [ReportItem.KeyRate7Year]: prevDetail[ReportItem.KeyRate7Year] ?? datum[ReportItem.KeyRate7Year] ?? null,
            [ReportItem.KeyRate8Year]: prevDetail[ReportItem.KeyRate8Year] ?? datum[ReportItem.KeyRate8Year] ?? null,
            [ReportItem.KeyRate9Month]: prevDetail[ReportItem.KeyRate9Month] ?? datum[ReportItem.KeyRate9Month] ?? null,
            [ReportItem.KeyRate9Year]: prevDetail[ReportItem.KeyRate9Year] ?? datum[ReportItem.KeyRate9Year] ?? null,
            [ReportItem.KeyRate10Year]: prevDetail[ReportItem.KeyRate10Year] ?? datum[ReportItem.KeyRate10Year] ?? null,
            [ReportItem.KeyRate15Year]: prevDetail[ReportItem.KeyRate15Year] ?? datum[ReportItem.KeyRate15Year] ?? null,
            [ReportItem.KeyRate0Year]: prevDetail[ReportItem.KeyRate0Year] ?? datum[ReportItem.KeyRate0Year] ?? null,
            [ReportItem.KeyRate25Year]: prevDetail[ReportItem.KeyRate25Year] ?? datum[ReportItem.KeyRate25Year] ?? null,
            [ReportItem.KeyRate30Year]: prevDetail[ReportItem.KeyRate30Year] ?? datum[ReportItem.KeyRate30Year] ?? null,
            [ReportItem.AssetType]: prevDetail[ReportItem.AssetType] ?? datum[ReportItem.AssetType] ?? null,
            [ReportItem.AssetClass]: prevDetail[ReportItem.AssetClass] ?? datum[ReportItem.AssetClass] ?? null,
            [ReportItem.CountryofIncorp]: prevDetail[ReportItem.CountryofIncorp] ?? datum[ReportItem.CountryofIncorp] ?? null,
            [ReportItem.CountryofRisk]: prevDetail[ReportItem.CountryofRisk] ?? datum[ReportItem.CountryofRisk] ?? null,
            [ReportItem.BaseCurrencyName]: prevDetail[ReportItem.BaseCurrencyName] ?? datum[ReportItem.BaseCurrencyName] ?? null,
            [ReportItem.CUSIP9]: prevDetail[ReportItem.CUSIP9] ?? datum[ReportItem.CUSIP9] ?? null,
            [ReportItem.ExchangeTicker]: prevDetail[ReportItem.ExchangeTicker] ?? datum[ReportItem.ExchangeTicker] ?? null,
            [ReportItem.ISIN12]: prevDetail[ReportItem.ISIN12] ?? datum[ReportItem.ISIN12] ?? null,
            [ReportItem.SEDOL7]: prevDetail[ReportItem.SEDOL7] ?? datum[ReportItem.SEDOL7] ?? null
          }
          return nextDetail;
        }, detail);
        parsedBatches.push(combinedDetail);
      });
      cache[apiCallId] = parsedBatches;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }
  async getOneYearTrailingRisk(reportDate: Date) {
    const apiCallId = "riskOneYearHistory";
    if (this.reportId === null) return null;
    let result: ReportAPIResult = {
      resultKey: apiCallId,
      fromCache: false,
      empty: false
    };
    const cache = this.getCache();
    const cachePull = cache[apiCallId];
    if (cachePull !== null) {
      result.fromCache = true;
      return result;
    }
    try {
      const monthStr = (reportDate.getMonth()+1).toString().padStart(2, "0");
      const dayStr = reportDate.getDate().toString().padStart(2, "0");
      const response = await this.getAxiosResponse("v1/gfr/getRiskHistory", {
        beginDate: `${reportDate.getFullYear() - 1}-${monthStr}-${dayStr}`,
        endDate: `${reportDate.getFullYear()}-${monthStr}-${dayStr}`
      });
      if (response === null || !response.data) {
        result.empty = true;
        result.error = "Data not found";
        return result;
      }
      const data: RiskHistoryResultDatum[] = response.data;
      const parsedData: RiskHistoryData = {
        historicalData: [],
        earliestDateAvailable: reportDate,
        latestDateAvailable: reportDate
      };
      data.forEach(datum => {
        let analysisDate = new Date(datum.analysisDate);
        parsedData.historicalData.push({
          ...datum,
          analysisDate
        });
        if (analysisDate < parsedData.earliestDateAvailable) {
          parsedData.earliestDateAvailable = analysisDate;
        }
      });
      if (!data.length) {
        result.empty = true;
      }
      parsedData.historicalData.sort((a,b) => a.analysisDate.getTime() - b.analysisDate.getTime());
      cache[apiCallId] = parsedData;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }

  // Per-period factor contribution at the given frequency.
  async getGfpaFactorContributionWithFreq(
    beginDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs,
    frequency: "D" | "W" | "M" | "Y" = "D",
  ) {
    if (this.reportId === null) return null;
    const cacheKey = `${RADashboardDataService.makeAttributionCacheKey(this.reportId, beginDate, endDate)}:${frequency}`;
    const apiCallId = "factorContributionFreq";
    let result: AttributionAPIResult<IGfpaFactorContributionFreq[]> = {
      resultKey: apiCallId,
      data: null,
      fromCache: false,
      empty: false
    };
    const cachePull = RADashboardDataService.lruGet(this.factorFreqCache, cacheKey);
    if (cachePull !== undefined) {
      result.fromCache = true;
      result.data = cachePull;
      return result;
    }
    try {
      const response = await this.getAxiosResponse(`v1/gfpa/factor-contribution-with-freq`, {
        beginDate: beginDate.format("YYYYMMDD"),
        endDate: endDate.format("YYYYMMDD"),
        frequency,
      });
      if (response === null || !response.data) {
        result.empty = true;
        result.error = "Null Response";
        return result;
      }
      // The detail endpoint returns contributions in basis points; convert to percent so the app is
      // consistent with the totals endpoint (which is already in percent).
      const bpToPct = (v: number | null) => (v == null ? null : v / 100);
      const rows = (response.data as IGfpaFactorContributionFreq[]).map((r) => ({
        ...r,
        portfolio_total_return: bpToPct(r.portfolio_total_return),
        benchmark_total_return: bpToPct(r.benchmark_total_return),
        active_total_return: bpToPct(r.active_total_return),
      }));
      if (!rows.length) result.empty = true;
      RADashboardDataService.lruSet(this.factorFreqCache, cacheKey, rows, RADashboardDataService.FACTOR_FREQ_CACHE_LIMIT);
      result.data = rows;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }

  // Per-factor totals over the whole range (no frequency): one row per leaf factor. Drives the strip
  // and table, where the backend's aggregate is used directly rather than summing the per-period data.
  async getGfpaFactorContribution(beginDate: dayjs.Dayjs, endDate: dayjs.Dayjs) {
    if (this.reportId === null) return null;
    const cacheKey = RADashboardDataService.makeAttributionCacheKey(this.reportId, beginDate, endDate);
    const apiCallId = "factorContributionTotal";
    let result: AttributionAPIResult<IGfpaFactorContributionTotal[]> = {
      resultKey: apiCallId,
      data: null,
      fromCache: false,
      empty: false
    };
    const cachePull = RADashboardDataService.lruGet(this.factorTotalCache, cacheKey);
    if (cachePull !== undefined) {
      result.fromCache = true;
      result.data = cachePull;
      return result;
    }
    try {
      const response = await this.getAxiosResponse(`v1/gfpa/factor-contribution`, {
        beginDate: beginDate.format("YYYYMMDD"),
        endDate: endDate.format("YYYYMMDD"),
      });
      if (response === null || !response.data) {
        result.empty = true;
        result.error = "Null Response";
        return result;
      }
      // Same basis-point-to-percent conversion as the per-frequency endpoint, so both stay in percent.
      const bpToPct = (v: number | null) => (v == null ? null : v / 100);
      const rows = (response.data as IGfpaFactorContributionTotal[]).map((r) => ({
        ...r,
        portfolio_total_return: bpToPct(r.portfolio_total_return),
        benchmark_total_return: bpToPct(r.benchmark_total_return),
        active_total_return: bpToPct(r.active_total_return),
      }));
      if (!rows.length) result.empty = true;
      RADashboardDataService.lruSet(this.factorTotalCache, cacheKey, rows, RADashboardDataService.FACTOR_TOTAL_CACHE_LIMIT);
      result.data = rows;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }

  // Per-security return contribution (gfpa/security-details endpoint) over the given range.
  async getGfpaSecurityDetails(beginDate: dayjs.Dayjs, endDate: dayjs.Dayjs) {
    if (this.reportId === null) return null;
    const cacheKey = RADashboardDataService.makeAttributionCacheKey(this.reportId, beginDate, endDate);
    const apiCallId = "securityContribution";
    let result: AttributionAPIResult<IGfpaSecurityContrib[]> = {
      resultKey: apiCallId,
      data: null,
      fromCache: false,
      empty: false
    };
    const cachePull = RADashboardDataService.lruGet(this.securityContribCache, cacheKey);
    if (cachePull !== undefined) {
      result.fromCache = true;
      result.data = cachePull;
      return result;
    }
    try {
      // The endpoint caps the item list, so request the items in parallel batches. StandardID is requested
      // in every batch and used as the merge key (positional correlation would misattribute data if a
      // batch omits securities), and the merge is null-safe so a batch can't overwrite a filled field.
      const begin = Number(beginDate.format("YYYYMMDD"));
      const end = Number(endDate.format("YYYYMMDD"));
      const otherItems = SECURITY_CONTRIB_ITEMS.filter((x) => x !== ReportItem.StandardID);
      const itemBatches: (typeof SECURITY_CONTRIB_ITEMS)[] = [];
      for (let i = 0; i < otherItems.length; i += 29) itemBatches.push([ReportItem.StandardID, ...otherItems.slice(i, i + 29)]);
      const responses = await Promise.all(
        itemBatches.map((items) =>
          this.postAxiosResponse("v1/gfpa/security-details", { beginDate: begin, endDate: end, items }),
        ),
      );
      // Bail loudly on any missing/empty batch rather than silently returning partial columns.
      if (responses.some((r) => r === null || !r.data || !(r.data as IGfpaSecurityContrib[]).length)) {
        result.empty = true;
        result.error = "Null Response";
        return result;
      }
      const byId = new Map<string, IGfpaSecurityContrib>();
      const order: string[] = [];
      for (const r of responses) {
        for (const row of r!.data as IGfpaSecurityContrib[]) {
          const id = row[ReportItem.StandardID];
          if (id == null) continue; // no identifier to correlate on — skip rather than misattribute
          const existing = byId.get(id);
          if (!existing) { byId.set(id, { ...row }); order.push(id); continue; }
          for (const key of Object.keys(row) as (keyof IGfpaSecurityContrib)[]) {
            if (existing[key] == null && row[key] != null) (existing[key] as unknown) = row[key];
          }
        }
      }
      const rows = order.map((id) => byId.get(id)!);
      if (!rows.length) result.empty = true;
      RADashboardDataService.lruSet(this.securityContribCache, cacheKey, rows, RADashboardDataService.SECURITY_CONTRIB_CACHE_LIMIT);
      result.data = rows;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }

  // `zoom` routes the arbitrary range through the bounded LRU instead of the base-range attrCaches.
  async getDateRangeTotalReturn(beginDate: dayjs.Dayjs, endDate: dayjs.Dayjs, zoom = false) {
    if (this.reportId === null) return null;
    const cacheKey = RADashboardDataService.makeAttributionCacheKey(this.reportId, beginDate, endDate);
    const apiCallId = "totalReturn";
    let result: AttributionAPIResult<AttributionTotalReturnsData> = {
      resultKey: apiCallId,
      data: null,
      fromCache: false,
      empty: false
    };
    const cachePull = zoom
      ? RADashboardDataService.lruGet(this.totalReturnZoomCache, cacheKey) ?? null
      : this.getAttrCache(cacheKey)[apiCallId];
    if (cachePull !== null && cachePull !== undefined) {
      result.fromCache = true;
      result.data = cachePull;
      return result;
    }
    try {
      const response = await this.getAxiosResponse(`v1/report/totalReturn`, {
        reportType: "GFPA",
        beginDate: beginDate.format("YYYYMMDD"),
        endDate: endDate.format("YYYYMMDD")
      });
      if (response === null) {
        result.empty = true;
        result.error = "Null Response";
        return result;
      }
      const data: AttributionTotalReturnsResult = response.data;
      const parsedData: AttributionTotalReturnsData = {
        portfolioContribution: data.total_ret_contrib_p,
        benchmarkContribution: data.total_ret_contrib_b,
        differenceContribution: data.total_ret_contrib_d
      };
      if (zoom) {
        RADashboardDataService.lruSet(this.totalReturnZoomCache, cacheKey, parsedData, RADashboardDataService.TOTAL_RETURN_CACHE_LIMIT);
      } else {
        this.getAttrCache(cacheKey)[apiCallId] = parsedData;
      }
      result.data = parsedData;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }

  async getDateRangeDuration(beginDate: dayjs.Dayjs, endDate: dayjs.Dayjs) {
    if (this.reportId === null) return null;
    const cacheKey = RADashboardDataService.makeAttributionCacheKey(this.reportId, beginDate, endDate);
    const apiCallId = "attrDurations";
    let result: AttributionAPIResult<AttributionDurationData> = {
      resultKey: apiCallId,
      data: null,
      fromCache: false,
      empty: false
    };
    const cache = this.getAttrCache(cacheKey);
    const cachePull = cache[apiCallId];
    if (cachePull !== null) {
      result.fromCache = true;
      result.data = cachePull;
      return result;
    }
    try {
      const response = await this.getAxiosResponse(`v1/report/duration`, {
        reportType: "GFPA",
        beginDate: beginDate.format("YYYYMMDD"),
        endDate: endDate.format("YYYYMMDD")
      });
      if (response === null) {
        result.empty = true;
        result.error = "Null Response";
        return result;
      }
      const data: AttributionDurationResult = response.data;
      const parsedData: AttributionDurationData = {
        portfolio: {
          effective: data.edur_p,
          spread: data.sdur_p
        },
        benchmark: {
          effective: data.edur_b,
          spread: data.sdur_b
        }
      };
      cache[apiCallId] = parsedData;
      result.data = parsedData;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }

  async getGfpaSummaries(beginDate: dayjs.Dayjs, endDate: dayjs.Dayjs) {
    if (this.reportId === null) return null;
    //const cacheKey = RADashboardDataService.makeAttributionCacheKey(this.reportId, beginDate, endDate);
    const cacheKey = this.reportId.toString();
    const apiCallId = "dailySummaries";
    let result: AttributionAPIResult<Record<string, AttributionGfpaSummaryDatum>> = {
      resultKey: apiCallId,
      data: null,
      fromCache: false,
      empty: false
    };
    const cache = this.getAttrCache(cacheKey);
    const cachePull = cache[apiCallId];
    if (cachePull !== null) { // There are cached summaries. See if we have all we need.
      // Summaries are keyed under their date formatted YYYY-MM-DD
      const cacheData: Record<string, AttributionGfpaSummaryDatum> = {};
      result.fromCache = true; // Assume the cache knows all the days.
      result.empty = true;
      // Loop through the attribution range to verify each day exists and add it to the return value.
      for (let d = beginDate; d.isBefore(endDate); d = nextWeekday(d)) {
        const dayKey = d.format("YYYY-MM-DD");
        if (dayKey in cachePull) {
          result.empty = false;
          cacheData[dayKey] = cachePull[dayKey];
        } else {
          // If a day is missing, the cache is invalid.
          result.fromCache = false;
          break;
        }
      }
      if (result.fromCache) {
        result.data = cacheData;
        return result;
      }
    }
    try {
      const response = await this.getAxiosResponse(`v1/gfpa/summary`, {
        reportId: this.reportId,
        beginDate: beginDate.format("YYYYMMDD"),
        endDate: endDate.format("YYYYMMDD")
      });
      if (response === null || !response.data) {
        result.empty = true;
        result.error = "Null Response";
        return result;
      }
      const data: AttributionGfpaSummaryResult[] = response.data;
      console.log(`[${apiCallId}] Raw: `, data);
      const parsedData: Record<string, AttributionGfpaSummaryDatum> = {};
      data.forEach(res => {
        const datum: AttributionGfpaSummaryDatum = {
          date: new Date(res.date_col),
          portfolioReturn: res.p,
          benchmarkReturn: res.b,
          activeReturn: res.d,
          portfolioExposure: res?.portfolio_weighted_exposure ?? null,
          benchmarkExposure: res?.benchmark_weighted_exposure ?? null,
          reportId: res?.report_id ?? null,
        };
        parsedData[res.date_col] = datum;
      });
      // Add new summaries to the cache
      cache[apiCallId] = {
        ...cache[apiCallId],
        ...parsedData
      };
      result.data = parsedData;
    } catch (err) {
      result.error = parseApiError(err);
    }
    return result;
  }

  static sortFactorContribData(data: FactorContrib[]) {
    const result: FactorLensResult = {
      "portfolio": {
        factors: [],
        levels: {
          0: [],
          1: [],
          2: [],
          3: [],
          4: [],
          5: []
        }
      },
      "benchmark": {
        factors: [],
        levels: {
          0: [],
          1: [],
          2: [],
          3: [],
          4: [],
          5: []
        }
      },
      "active": {
        factors: [],
        levels: {
          0: [],
          1: [],
          2: [],
          3: [],
          4: [],
          5: []
        }
      },
      portfolioSecurities: []
    };
    data.forEach(datum => {
      let dataSet: FactorLensDataSet | null = null;
      switch (datum.data_set) {
        case ReportDataSet.Portfolio:
          dataSet = result.portfolio; break;
        case ReportDataSet.Benchmark:
          dataSet = result.benchmark; break;
        case ReportDataSet.Active:
          dataSet = result.active; break;
      }
      if (dataSet != null) {
        datum.risk_contrib /= 100; //Convert bps to %
        
        dataSet.factors.push(datum);

        this.addToFactorLevelTotal(dataSet.levels[0], "Total Risk", datum.risk_contrib);
        this.addToFactorLevelTotal(dataSet.levels[1], datum.level1, datum.risk_contrib);
        this.addToFactorLevelTotal(dataSet.levels[2], datum.level2, datum.risk_contrib);
        this.addToFactorLevelTotal(dataSet.levels[3], datum.level3, datum.risk_contrib);
        this.addToFactorLevelTotal(dataSet.levels[4], datum.level4, datum.risk_contrib);
        this.addToFactorLevelTotal(dataSet.levels[5], datum.level5, datum.risk_contrib);
      }
    });
    return result;
  }

  static addToFactorLevelTotal(currentState: FactorLevelGroup[], groupName: string, riskContrib: number)
  {
    if(groupName === null || groupName ==="")
    {
      return;
    }

    const existing = currentState.find(level => level.name === groupName);
    if (existing) {
      existing.riskContrib += riskContrib;
    } else {
      currentState.push({ name: groupName, riskContrib });
    }
  }

  static makeAttributionCacheKey(reportId: number, beginDate: dayjs.Dayjs, endDate: dayjs.Dayjs) {
    return [reportId, beginDate.format("YYYYMMDD"), endDate.format("YYYYMMDD")].join("~");
  }
}

export const raDashboardDataService = new RADashboardDataService();
export default raDashboardDataService;